import html2pdf from 'html2pdf.js';

/**
 * Strips metadata from PDF blob to prevent information disclosure
 * This removes Producer, Creator, and other identifying metadata
 * @param pdfBlob - The original PDF blob
 * @returns A new PDF blob with metadata removed
 */
const stripPdfMetadata = async (pdfBlob: Blob): Promise<Blob> => {
  try {
    // Read the PDF as array buffer
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    let pdfString = new TextDecoder('latin1').decode(uint8Array);
    
    // Remove Producer metadata (jsPDF version information)
    pdfString = pdfString.replace(/\/Producer\s*\([^)]*\)/g, '/Producer ()');
    
    // Remove Creator metadata
    pdfString = pdfString.replace(/\/Creator\s*\([^)]*\)/g, '/Creator ()');
    
    // Remove CreationDate and ModDate to prevent date fingerprinting
    pdfString = pdfString.replace(/\/CreationDate\s*\([^)]*\)/g, '/CreationDate ()');
    pdfString = pdfString.replace(/\/ModDate\s*\([^)]*\)/g, '/ModDate ()');
    
    // Convert back to blob using latin1 encoding to preserve binary data
    const strippedArray = new Uint8Array(pdfString.length);
    for (let i = 0; i < pdfString.length; i++) {
      strippedArray[i] = pdfString.charCodeAt(i) & 0xff;
    }
    return new Blob([strippedArray], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error stripping PDF metadata:', error);
    // If stripping fails, return original blob
    return pdfBlob;
  }
};

export const downloadPDF = (pdfBlob: Blob) => {
  // Create a URL for the blob
  const url = window.URL.createObjectURL(pdfBlob);

  // Create a temporary link element
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Informe de valoración.pdf';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL
  window.URL.revokeObjectURL(url);
};

const toProxyUrl = (src: string): string | null => {
  if (!src || src.startsWith('data:') || src.startsWith('/')) return null;
  try {
    const url = new URL(src);
    if (url.origin === window.location.origin) return null;
    return `/image-proxy/${url.host}${url.pathname}${url.search}`;
  } catch {
    return null;
  }
};

const preloadImage = (img: HTMLImageElement): Promise<string | null> => {
  return new Promise((resolve) => {
    const proxySrc = toProxyUrl(img.src);
    if (!proxySrc) {
      resolve(null);
      return;
    }
    const originalSrc = img.src;
    img.onload = () => resolve(originalSrc);
    img.onerror = () => {
      img.src = originalSrc; // revert immediately so the browser stays unaffected
      resolve(null);
    };
    img.src = proxySrc;
  });
};

export const handlePDFGeneration = async () => {
  let images: HTMLImageElement[] = [];
  let originalSrcs: (string | null)[] = [];
  try {
    const element = document.querySelector('.app-gp');
    if (!element) return;

    // Rewrite cross-origin image srcs to same-origin proxy URLs before html2canvas
    images = Array.from(element.getElementsByTagName('img'));
    originalSrcs = await Promise.all(images.map(preloadImage));

    const opt = {
      filename: 'Informe de valoración.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        allowTaint: true,
        imageTimeout: 0,
        onclone: (clonedDoc: Document) => {
          // Ensure images maintain their aspect ratio in the cloned document
          const images = clonedDoc.getElementsByTagName('img');
          Array.from(images).forEach((img: HTMLImageElement) => {
            img.style.transform = 'none';
            img.style.maxWidth = '100%';
            img.style.maxHeight = '100%';
          });
        },
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        // Security: Strip metadata to prevent information disclosure
        putOnlyUsedFonts: true,
        compress: true
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    // Generate PDF as Blob
    const worker = html2pdf().set(opt).from(element);
    const pdfBlob = await worker.outputPdf('blob');

    // Restore original srcs
    images.forEach((img, i) => { if (originalSrcs[i]) img.src = originalSrcs[i]!; });

    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    if (isInIframe) {
      window.parent.postMessage({ type: 'TRIGGER_DOWNLOAD_RECEIVED' }, '*');
      // If in iframe, send message to parent
      const reader = new FileReader();
      reader.onloadend = function () {
        window.parent.postMessage(
          {
            type: 'DOWNLOAD_REPORT',
            filename: 'Informe de valoración.pdf',
            dataUrl: reader.result,
          },
          '*'
        );
      };
      reader.readAsDataURL(pdfBlob);
    } else {
      // If not in iframe, download directly
      downloadPDF(pdfBlob);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    images.forEach((img, i) => { if (originalSrcs[i]) img.src = originalSrcs[i]!; });
  }
};
